import React from 'react';
import {
    // Action Icons
    SaveOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    MinusOutlined,
    CheckOutlined,
    CloseOutlined,
    StopOutlined,
    ReloadOutlined,
    SyncOutlined,
    UndoOutlined,
    RedoOutlined,

    // File & Document Icons
    FileOutlined,
    FileTextOutlined,
    FilePdfOutlined,
    FileExcelOutlined,
    FileWordOutlined,
    FileImageOutlined,
    FileZipOutlined,
    FolderOutlined,
    FolderOpenOutlined,

    // Navigation Icons
    LeftOutlined,
    RightOutlined,
    UpOutlined,
    DownOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    DoubleLeftOutlined,
    DoubleRightOutlined,

    // Communication Icons
    MailOutlined,
    MessageOutlined,
    CommentOutlined,
    PhoneOutlined,
    SendOutlined,
    NotificationOutlined,
    BellOutlined,

    // User & Team Icons
    UserOutlined,
    TeamOutlined,
    UserAddOutlined,
    UserDeleteOutlined,
    UsergroupAddOutlined,
    UsergroupDeleteOutlined,
    ContactsOutlined,

    // Data Icons
    DownloadOutlined,
    UploadOutlined,
    ExportOutlined,
    ImportOutlined,
    CloudUploadOutlined,
    CloudDownloadOutlined,
    DatabaseOutlined,

    // UI Control Icons
    SearchOutlined,
    FilterOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined,
    ExpandOutlined,
    CompressOutlined,
    MenuOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,

    // Status Icons
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    QuestionCircleOutlined,
    WarningOutlined,
    IssuesCloseOutlined,
    ClockCircleOutlined,

    // Business Icons
    DollarOutlined,
    EuroOutlined,
    ShoppingCartOutlined,
    ShopOutlined,
    GiftOutlined,
    TrophyOutlined,
    ProjectOutlined,
    CalendarOutlined,
    ScheduleOutlined,

    // Settings & Tools Icons
    SettingOutlined,
    ToolOutlined,
    BuildOutlined,
    ControlOutlined,
    ApiOutlined,
    CodeOutlined,
    BugOutlined,
    ExperimentOutlined,

    // Media Icons
    CameraOutlined,
    VideoCameraOutlined,
    AudioOutlined,
    PictureOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,

    // Other Common Icons
    HomeOutlined,
    LockOutlined,
    UnlockOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    LinkOutlined,
    DisconnectOutlined,
    GlobalOutlined,
    EnvironmentOutlined,
    PrinterOutlined,
    ScanOutlined,
    QrcodeOutlined,
    BarcodeOutlined,
    CopyOutlined,
    ScissorOutlined,
    FormOutlined,
    HighlightOutlined,
    DiffOutlined,
    SnippetsOutlined
} from '@ant-design/icons';
import logger from '../../utils/logger';



/**
 * Comprehensive Icon Mapping for the Design System
 *
 * This file provides a centralized mapping of icon names to icon components,
 * organized by category for easy discovery and consistent usage across the application.
 *
 * Usage:
 * import { ICON_MAP, getIcon } from '@/design-system/constants/iconMap';
 *
 * // Direct usage
 * const SaveIcon = ICON_MAP.ACTION.SAVE;
 *
 * // Dynamic usage
 * const icon = getIcon('ACTION', 'SAVE');
 */

// Icon mapping organized by category
export const ICON_MAP = {
    ACTION: {
        SAVE: <SaveOutlined />,
        EDIT: <EditOutlined />,
        DELETE: <DeleteOutlined />,
        ADD: <PlusOutlined />,
        REMOVE: <MinusOutlined />,
        CHECK: <CheckOutlined />,
        CLOSE: <CloseOutlined />,
        CANCEL: <CloseOutlined />,
        STOP: <StopOutlined />,
        REFRESH: <ReloadOutlined />,
        SYNC: <SyncOutlined />,
        UNDO: <UndoOutlined />,
        REDO: <RedoOutlined />
    },

    FILE: {
        DEFAULT: <FileOutlined />,
        TEXT: <FileTextOutlined />,
        PDF: <FilePdfOutlined />,
        EXCEL: <FileExcelOutlined />,
        WORD: <FileWordOutlined />,
        IMAGE: <FileImageOutlined />,
        ZIP: <FileZipOutlined />,
        FOLDER: <FolderOutlined />,
        FOLDER_OPEN: <FolderOpenOutlined />
    },

    NAVIGATION: {
        LEFT: <LeftOutlined />,
        RIGHT: <RightOutlined />,
        UP: <UpOutlined />,
        DOWN: <DownOutlined />,
        ARROW_LEFT: <ArrowLeftOutlined />,
        ARROW_RIGHT: <ArrowRightOutlined />,
        ARROW_UP: <ArrowUpOutlined />,
        ARROW_DOWN: <ArrowDownOutlined />,
        FIRST: <DoubleLeftOutlined />,
        LAST: <DoubleRightOutlined />
    },

    COMMUNICATION: {
        EMAIL: <MailOutlined />,
        MESSAGE: <MessageOutlined />,
        COMMENT: <CommentOutlined />,
        PHONE: <PhoneOutlined />,
        SEND: <SendOutlined />,
        NOTIFICATION: <NotificationOutlined />,
        BELL: <BellOutlined />
    },

    USER: {
        SINGLE: <UserOutlined />,
        GROUP: <TeamOutlined />,
        ADD: <UserAddOutlined />,
        REMOVE: <UserDeleteOutlined />,
        GROUP_ADD: <UsergroupAddOutlined />,
        GROUP_REMOVE: <UsergroupDeleteOutlined />,
        CONTACTS: <ContactsOutlined />
    },

    DATA: {
        DOWNLOAD: <DownloadOutlined />,
        UPLOAD: <UploadOutlined />,
        EXPORT: <ExportOutlined />,
        IMPORT: <ImportOutlined />,
        CLOUD_UPLOAD: <CloudUploadOutlined />,
        CLOUD_DOWNLOAD: <CloudDownloadOutlined />,
        DATABASE: <DatabaseOutlined />
    },

    UI: {
        SEARCH: <SearchOutlined />,
        FILTER: <FilterOutlined />,
        SORT_ASC: <SortAscendingOutlined />,
        SORT_DESC: <SortDescendingOutlined />,
        FULLSCREEN: <FullscreenOutlined />,
        EXIT_FULLSCREEN: <FullscreenExitOutlined />,
        EXPAND: <ExpandOutlined />,
        COMPRESS: <CompressOutlined />,
        MENU: <MenuOutlined />,
        MENU_FOLD: <MenuFoldOutlined />,
        MENU_UNFOLD: <MenuUnfoldOutlined />
    },

    STATUS: {
        SUCCESS: <CheckCircleOutlined />,
        ERROR: <CloseCircleOutlined />,
        WARNING: <ExclamationCircleOutlined />,
        INFO: <InfoCircleOutlined />,
        QUESTION: <QuestionCircleOutlined />,
        ALERT: <WarningOutlined />,
        CLOSED: <IssuesCloseOutlined />,
        PENDING: <ClockCircleOutlined />
    },

    BUSINESS: {
        DOLLAR: <DollarOutlined />,
        EURO: <EuroOutlined />,
        CART: <ShoppingCartOutlined />,
        SHOP: <ShopOutlined />,
        GIFT: <GiftOutlined />,
        TROPHY: <TrophyOutlined />,
        PROJECT: <ProjectOutlined />,
        CALENDAR: <CalendarOutlined />,
        SCHEDULE: <ScheduleOutlined />
    },

    SETTINGS: {
        GEAR: <SettingOutlined />,
        TOOL: <ToolOutlined />,
        BUILD: <BuildOutlined />,
        CONTROL: <ControlOutlined />,
        API: <ApiOutlined />,
        CODE: <CodeOutlined />,
        BUG: <BugOutlined />,
        EXPERIMENT: <ExperimentOutlined />
    },

    MEDIA: {
        CAMERA: <CameraOutlined />,
        VIDEO: <VideoCameraOutlined />,
        AUDIO: <AudioOutlined />,
        PICTURE: <PictureOutlined />,
        PLAY: <PlayCircleOutlined />,
        PAUSE: <PauseCircleOutlined />
    },

    MISC: {
        HOME: <HomeOutlined />,
        LOCK: <LockOutlined />,
        UNLOCK: <UnlockOutlined />,
        EYE: <EyeOutlined />,
        EYE_INVISIBLE: <EyeInvisibleOutlined />,
        LINK: <LinkOutlined />,
        DISCONNECT: <DisconnectOutlined />,
        GLOBAL: <GlobalOutlined />,
        LOCATION: <EnvironmentOutlined />,
        PRINT: <PrinterOutlined />,
        SCAN: <ScanOutlined />,
        QRCODE: <QrcodeOutlined />,
        BARCODE: <BarcodeOutlined />,
        COPY: <CopyOutlined />,
        CUT: <ScissorOutlined />,
        FORM: <FormOutlined />,
        HIGHLIGHT: <HighlightOutlined />,
        DIFF: <DiffOutlined />,
        SNIPPETS: <SnippetsOutlined />
    }
};

/**
 * Helper function to get icon by category and name
 * @param {string} category - The category of the icon (e.g., 'ACTION', 'FILE')
 * @param {string} name - The name of the icon within the category (e.g., 'SAVE', 'DELETE')
 * @returns {React.Component|null} The icon component or null if not found
 */
export const getIcon = (category, name) => {
    if (!ICON_MAP[category] || !ICON_MAP[category][name]) {
        logger.warn(`Icon not found: ${category}.${name}`);
        return null;
    }
    return ICON_MAP[category][name];
};

/**
 * Commonly used icon combinations for specific contexts
 */
export const ICON_CONTEXTS = {
    // Modal actions
    MODAL_SAVE: ICON_MAP.ACTION.SAVE,
    MODAL_CANCEL: ICON_MAP.ACTION.CANCEL,
    MODAL_DELETE: ICON_MAP.ACTION.DELETE,
    MODAL_CLOSE: ICON_MAP.ACTION.CLOSE,

    // Table actions
    TABLE_ADD: ICON_MAP.ACTION.ADD,
    TABLE_EDIT: ICON_MAP.ACTION.EDIT,
    TABLE_DELETE: ICON_MAP.ACTION.DELETE,
    TABLE_SEARCH: ICON_MAP.UI.SEARCH,
    TABLE_FILTER: ICON_MAP.UI.FILTER,
    TABLE_EXPORT: ICON_MAP.DATA.EXPORT,

    // Form actions
    FORM_SUBMIT: ICON_MAP.ACTION.CHECK,
    FORM_RESET: ICON_MAP.ACTION.UNDO,
    FORM_CANCEL: ICON_MAP.ACTION.CANCEL,
    FORM_SAVE: ICON_MAP.ACTION.SAVE,

    // File operations
    FILE_UPLOAD: ICON_MAP.DATA.UPLOAD,
    FILE_DOWNLOAD: ICON_MAP.DATA.DOWNLOAD,
    FILE_DELETE: ICON_MAP.ACTION.DELETE,
    FILE_VIEW: ICON_MAP.MISC.EYE,

    // User operations
    USER_ADD: ICON_MAP.USER.ADD,
    USER_EDIT: ICON_MAP.ACTION.EDIT,
    USER_DELETE: ICON_MAP.USER.REMOVE,
    USER_VIEW: ICON_MAP.USER.SINGLE,

    // Status indicators
    STATUS_ACTIVE: ICON_MAP.STATUS.SUCCESS,
    STATUS_INACTIVE: ICON_MAP.STATUS.ERROR,
    STATUS_PENDING: ICON_MAP.STATUS.PENDING,
    STATUS_WARNING: ICON_MAP.STATUS.WARNING
};

/**
 * Icon size presets for consistency
 */
export const ICON_SIZES = {
    SMALL: 14,
    DEFAULT: 16,
    MEDIUM: 20,
    LARGE: 24,
    XLARGE: 32
};

// Export all icons for backward compatibility
export default ICON_MAP;